import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  HStack,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { format, subDays } from 'date-fns';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteJournal,
  getJournalByDate,
  saveJournal,
} from '../../stores/slices/journals/journalsActions';
import './date-picker.css';
import { FaCaretDown } from 'react-icons/fa';
import { addDays } from 'date-fns/esm';
import { Editor } from '@tinymce/tinymce-react';
import DeleteAlert from '../Alerts/DeleteAlert';
import API_URL from '../../utils/getAPIUrl';
import LoadingOverlay from '../LoadingOverlay';
import ShareModal from './ShareModal';
import TemplateModal from './TemplateModal';

const TINYMCE_API = process.env.REACT_APP_TINYMCE_KEY;

const Journal = () => {
  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <Button
      variant={'outline'}
      rightIcon={<FaCaretDown />}
      size={'sm'}
      onClick={onClick}
      ref={ref}
    >
      {value}
    </Button>
  ));
  const editorRef = useRef(null);
  const { journal, loading } = useSelector(state => state.journals);
  const { userToken } = useSelector(state => state.user);
  const [toUpdate, setToUpdate] = useState({ state: false, journal: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [editorValue, setEditorValue] = useState('');
  const cancelRef = useRef();
  const toast = useToast();
  const dispatch = useDispatch();
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const {
    isOpen: isOpenAlertOverride,
    onOpen: onOpenAlertOverride,
    onClose: onCloseAlertOverride,
  } = useDisclosure();
  const {
    isOpen: isOpenShareModal,
    onOpen: onOpenShareModal,
    onClose: onCloseShareModal,
  } = useDisclosure();
  const {
    isOpen: isOpenTemplate,
    onOpen: onOpenTemplate,
    onClose: onCloseTemplate,
  } = useDisclosure();

  useEffect(() => {
    dispatch(getJournalByDate({ date: format(currentDate, 'yyyy-MM-dd') }));
    // eslint-disable-next-line
  }, [currentDate]);

  useEffect(() => {
    if (editorRef.current && !loading) {
      editorRef.current.setContent(journal?.entry || '');
      editorRef.current.undoManager.clear();
    }
  }, [journal, editorRef, loading]);

  useEffect(() => {
    if (journal) {
      setToUpdate({ state: true, journal: journal });
    } else {
      setToUpdate({ state: false, journal: null });
    }
  }, [journal]);

  const nextDay = () => {
    setCurrentDate(addDays(currentDate, 1));
  };
  const previousDay = () => {
    setCurrentDate(subDays(currentDate, 1));
  };
  const closeTemplateModal = template => {
    if (template && editorRef.current) {
      editorRef.current.setContent(template.entry);
      editorRef.current.undoManager.clear();
    }
    onCloseTemplate();
  };
  const submitJournal = async () => {
    if (editorRef.current) {
      const res = await dispatch(
        saveJournal({
          entry: editorRef.current.getContent(),
          dateOfEntry: currentDate,
          isUpdate: toUpdate,
        })
      );

      if (res.type === 'journals/saveJournal/rejected') {
        toast({
          title: 'Something went wrong',
          description: 'Please try again later.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        toast({
          description: toUpdate.state
            ? 'Journal has been updated'
            : 'Journal has been created',
          colorScheme: 'primary',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
          variant: 'primary',
        });
      }
    }
  };

  const onDeleteItem = async () => {
    setIsDeleting(true);
    const res = await dispatch(deleteJournal({ id: journal?.id }));
    await dispatch(
      getJournalByDate({ date: format(currentDate, 'yyyy-MM-dd') })
    );
    onCloseAlert();
    setIsDeleting(false);
    if (res.type === 'reminders/deleteReminder/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description: 'Item has been deleted',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const imageUploadHandler = (blobInfo, progress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;
      xhr.open('POST', `${API_URL}/journal_images`);
      xhr.setRequestHeader('Authorization', userToken);

      xhr.upload.onprogress = e => {
        progress((e.loaded / e.total) * 100);
      };

      xhr.onload = () => {
        if (xhr.status === 403) {
          reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject('HTTP Error: ' + xhr.status);
          return;
        }

        const json = JSON.parse(xhr.responseText);

        if (!json || typeof json.location != 'string') {
          reject('Invalid JSON: ' + xhr.responseText);
          return;
        }

        resolve(json.location);
      };

      xhr.onerror = () => {
        reject(
          'Image upload failed due to a XHR Transport error. Code: ' +
            xhr.status
        );
      };

      const formData = new FormData();
      formData.append(
        'journal_image[image_data]',
        blobInfo.blob(),
        blobInfo.filename()
      );

      xhr.send(formData);
    });

  return (
    <React.Fragment>
      <Box w={'full'} overflowX={'hidden'} overflowY={'auto'}>
        <VStack spacing={'4'} h={'full'}>
          <Stack
            justifyContent={'space-between'}
            alignContent={'center'}
            direction={{ base: 'column', sm: 'row' }}
            w={'full'}
            spacing={4}
            pr={'2'}
            pb={'2'}
            zIndex={'10'}
            sx={{
              position: '-webkit-sticky',
              /* Safari */
              position: 'sticky',
              top: '0',
            }}
            bg={'white'}
            borderBottom={'1px'}
            borderColor={'gray.500'}
          >
            <HStack>
              <Button
                variant={'outline'}
                border={'1.5px solid'}
                borderColor={'primary'}
                color={'primary'}
                size={'sm'}
                onClick={previousDay}
              >
                Previous
              </Button>
              <DatePicker
                showPopperArrow={false}
                selected={currentDate}
                onChange={date => setCurrentDate(date)}
                className="react-datapicker__input-text"
                dateFormat="MMMM d, yyyy"
                customInput={<ExampleCustomInput />}
              />
              <Button
                variant={'outline'}
                border={'1.5px solid'}
                borderColor={'primary'}
                color={'primary'}
                size={'sm'}
                onClick={nextDay}
              >
                Next
              </Button>
            </HStack>
            <HStack>
              <Button size={'sm'} disabled={loading} onClick={submitJournal}>
                Save
              </Button>
              <Button
                size={'sm'}
                disabled={loading || !journal}
                onClick={onOpenShareModal}
              >
                Share
              </Button>
              <Button
                size={'sm'}
                onClick={() => {
                  if (editorValue !== '') {
                    onOpenAlertOverride();
                  } else {
                    onOpenTemplate();
                  }
                }}
                disabled={loading}
              >
                Use Template
              </Button>
              <Button
                size={'sm'}
                disabled={!journal}
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={onOpenAlert}
              >
                Delete
              </Button>
            </HStack>
          </Stack>
          <Box
            overflowX={'auto'}
            w={'full'}
            flex={'1'}
            flexGrow={'1'}
            h={'full'}
            position={'relative'}
            border={'1px solid'}
            borderColor={'lightGray.500'}
            borderRadius={'6'}
          >
            {loading && <LoadingOverlay />}
            <Editor
              apiKey={TINYMCE_API}
              onInit={(evt, editor) => {
                editorRef.current = editor;
                editorRef.current.setContent(journal ? journal.entry : '');
                editorRef.current.undoManager.clear();
              }}
              initialValue={journal ? journal.entry : ''}
              onEditorChange={(value, editor) => {
                setEditorValue(value);
              }}
              init={{
                height: '100%',
                menubar: false,
                branding: false,
                plugins: [
                  'advlist',
                  'autolink',
                  'lists',
                  'link',
                  'image',
                  'charmap',
                  'preview',
                  'anchor',
                  'searchreplace',
                  'visualblocks',
                  'code',
                  'fullscreen',
                  'insertdatetime',
                  'media',
                  'table',
                  'help',
                  'wordcount',
                  'quickbars',
                ],
                toolbar:
                  'undo redo | blocks | ' +
                  'bold italic forecolor | alignleft aligncenter ' +
                  'alignright alignjustify | bullist numlist outdent indent | ' +
                  'removeformat | link image ',
                image_title: true,
                quickbars_image_toolbar:
                  'alignleft aligncenter alignright | rotateleft rotateright | imageoptions',
                automatic_uploads: true,
                images_upload_handler: imageUploadHandler,
                content_style:
                  'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              }}
            />
          </Box>
        </VStack>
      </Box>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isDeleting}
        header={`Delete journal entry?`}
        body={
          "Are you sure? If you proceed with the deletion, it'll be gone forever."
        }
        onDeleteItem={onDeleteItem}
      />

      <AlertDialog
        isOpen={isOpenAlertOverride}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlertOverride}
        blockScrollOnMount={false}
        closeOnOverlayClick={isDeleting ? false : true}
        closeOnEsc={isDeleting ? false : true}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Select from template?
            </AlertDialogHeader>

            <AlertDialogBody>
              You have contents on your journal, choosing a template will
              override your changes.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant={'outline'}
                onClick={onCloseAlertOverride}
              >
                Cancel
              </Button>
              <Button
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={() => {
                  onOpenTemplate();
                  onCloseAlertOverride();
                }}
                ml={3}
              >
                Okay
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {isOpenShareModal && (
        <ShareModal
          isOpen={isOpenShareModal}
          onOpen={onOpenShareModal}
          onClose={onCloseShareModal}
          journalId={journal.id}
        />
      )}

      <TemplateModal isOpen={isOpenTemplate} onClose={closeTemplateModal} />
    </React.Fragment>
  );
};

export default Journal;
